// Shared "access level + add-ons" model for the Set Permissions and Permission Presets
// screens. Both screens store loose booleans (with slightly different names), so this
// module works in the abstract AccessLevel/AddOn space and each screen maps to its own
// boolean shape.

export type AccessLevel = "viewOnly" | "editMenuData" | "editDesigns";
export type AddOn = "createTemplates" | "publish" | "orderPrint" | "holidayTemplates";

export interface AccessLevelDef {
  key: AccessLevel;
  label: string;
  description: string;
}

export interface AddOnDef {
  key: AddOn;
  label: string;
  chip: string;
  description: string;
  // Levels where the add-on is a real, toggleable checkbox.
  availableFor: AccessLevel[];
  // Levels where the add-on is granted implicitly (shown checked + disabled as "Included").
  includedFor: AccessLevel[];
  // Note shown under the checkbox when it is unavailable for the current level.
  requiresNote: string;
  drdOnly?: boolean;
}

export const ACCESS_LEVELS: AccessLevelDef[] = [
  {
    key: "viewOnly",
    label: "View Only",
    description: "Users can view and download existing designs but cannot edit or publish.",
  },
  {
    key: "editMenuData",
    label: "Edit Menu Data",
    description: "Users can update menu data on existing templates, make small spacing adjustments, and download the final output.",
  },
  {
    key: "editDesigns",
    label: "Edit Designs",
    description: "Full access to design and formatting tools in the editor. Users can edit designs, download files, and publish updates.",
  },
];

// Declaration order is the required display order: Create Templates, Publish, Order Print.
export const ADD_ONS: AddOnDef[] = [
  {
    key: "createTemplates",
    label: "Create Templates",
    chip: "Create Templates",
    description: "Users can create new templates from scratch or from existing designs.",
    availableFor: ["editDesigns"],
    includedFor: [],
    requiresNote: "Requires Edit Designs",
  },
  {
    key: "publish",
    label: "Publish",
    chip: "Publish",
    description: "Users can publish to QR codes, share links, displays, and embed codes.",
    availableFor: ["editMenuData"],
    includedFor: [],
    requiresNote: "Requires Edit Menu Data",
  },
  {
    key: "orderPrint",
    label: "Order Print",
    chip: "Order Print",
    description: "Allows user to place print orders from within MustHaveMenus.",
    availableFor: ["editMenuData", "editDesigns"],
    includedFor: [],
    requiresNote: "Requires Edit Menu Data or higher",
  },
  {
    key: "holidayTemplates",
    label: "Holiday Templates",
    chip: "Holiday Templates",
    description: "Shows the Templates menu in their dashboard with the Holiday Menus and Holiday Flyers collections only. Designs they start save to their community's Saved Designs.",
    availableFor: ["editMenuData"],
    includedFor: [],
    requiresNote: "Requires Edit Menu Data",
    drdOnly: true,
  },
];

export function addOnsFor(diningRDIntegration: boolean): AddOnDef[] {
  return ADD_ONS.filter((a) => !a.drdOnly || diningRDIntegration);
}

// On an account with Item Library enabled, "Edit Menu Data" covers two different ways of
// working, backed by two independent flags: itemLibraryOnly (data is maintained in the Item
// Library, with its channels and approvals) and dataOnly (data is edited straight on the
// design in the editor). They carry the same add-on rules, so both still map to the single
// "editMenuData" level above; only the label and description differ. Accounts without Item
// Library have one way to work and keep the plain label.
export const MENU_DATA_ITEM_LIBRARY = {
  label: "Edit Menu Data (Item Library)",
  description: "Users can update Item Library data in existing templates, make small spacing adjustments, and download the final output.",
};

// Shown for dataOnly only when it sits next to the Item Library option, so it has to say
// what makes it different. On its own the level keeps ACCESS_LEVELS' plain description.
export const MENU_DATA_IN_EDITOR_DESCRIPTION =
  "Users can update menu data directly on existing templates without going through the Item Library, make small spacing adjustments, and download the final output.";

// Permission presets store a single menu-data flag (dataOnly), so the preset screens always
// describe the in-editor variant. The Item Library variant is assignable per user + location.
export function accessLevelsFor(): AccessLevelDef[] {
  return ACCESS_LEVELS;
}

export function levelLabel(level: AccessLevel | null): string {
  return ACCESS_LEVELS.find((l) => l.key === level)?.label ?? "";
}

export type AddOnAvailability = "available" | "included" | "unavailable";

export function addOnAvailability(level: AccessLevel | null, addOn: AddOnDef): AddOnAvailability {
  if (level && addOn.availableFor.includes(level)) return "available";
  if (level && addOn.includedFor.includes(level)) return "included";
  return "unavailable";
}

// Checkbox checked state: real toggles reflect the selection, "included" reads as on.
export function isAddOnChecked(level: AccessLevel | null, addOn: AddOnDef, selected: AddOn[]): boolean {
  const availability = addOnAvailability(level, addOn);
  if (availability === "included") return true;
  if (availability === "available") return selected.includes(addOn.key);
  return false;
}

export function isAddOnDisabled(level: AccessLevel | null, addOn: AddOnDef): boolean {
  return addOnAvailability(level, addOn) !== "available";
}

// Note under a checkbox: "Included" when granted by the level, else the "Requires ..." hint.
export function addOnNote(level: AccessLevel | null, addOn: AddOnDef): string {
  const availability = addOnAvailability(level, addOn);
  if (availability === "included") return "Included";
  if (availability === "unavailable") return addOn.requiresNote;
  return "";
}

// Drop any selected add-ons that aren't real toggles for the given level.
export function clearInvalidAddOns(level: AccessLevel | null, selected: AddOn[]): AddOn[] {
  return ADD_ONS.filter(
    (a) => selected.includes(a.key) && addOnAvailability(level, a) === "available"
  ).map((a) => a.key);
}

// Plain-language summary, e.g. "Edit Menu Data · Publish · Order Print".
export function permissionSummary(level: AccessLevel | null, selected: AddOn[]): string {
  if (!level) return "No access";
  const parts = [levelLabel(level)];
  ADD_ONS.forEach((a) => {
    if (isAddOnChecked(level, a, selected)) parts.push(a.label);
  });
  return parts.join(" · ");
}
